// src/services/CalculatorService/CalculatorService.ts

import { AggregatorResponse } from "../../types/calculatorResponse.ts";
import { CALCULATOR_BASE_URL } from "../../constants/apiBases.ts";

export interface IncludeFlags {
  dallBoggBM?: boolean;
  dallBoggPremium?: boolean;
  dziBM: boolean;
  dziPremium: boolean;
  ozkBM: boolean;
  ozkPremium: boolean;
  generaliPremium: boolean;
  levInsVehicleData: boolean;
  levInsPremium: boolean;
  armeecPremium: boolean;
}

export interface CalcRequest {
  registrationPlate: string;
  certificateNumber: string;
  pin: string;
  ekatte: string;
  purpose: string;
  experience: number | "";
  accidents: boolean;
  maxWeightF1: number | "";
  include: IncludeFlags;
  /** Optional: omit for LIVE quotes (backend defaults all providers to live). */
  stubs?: IncludeFlags;
}

export interface NormalizedCalcRequest
  extends Omit<CalcRequest, "experience" | "maxWeightF1"> {
  experience: number;
  maxWeightF1: number;
}

export interface Installment {
  number: number;
  dueDate: string;
  amount: number;
  tax: number;
  totalAmount: number;
}

export interface ProviderQuote {
  providerId: string;
  grossPremium: number;
  taxes: number;
  bonusMalus: number;
  totalDue: number;
  currency: string;
  secondaryCurrency: string;
  instalments: Installment[];
  error?: { code: string; message: string };
}

export interface VehicleSnapshot {
  registrationPlate: string;
  chassis: string;
  make: string;
  model: string;
  usage: string;
  fuelType: string;
  bodyType: string;
  enginePower: number;
  engineDisplacement: number;
  seats: number;
  prodYear: number;
  carAge: number;
  firstRegistrationDate: string;
}

export interface OwnerSnapshot {
  name: string;
  identifier: string;
  cityCode: string;
}

export interface EnrichmentLog {
  provider: string;
  action: string;
  status: string;
  message: string;
}

export interface CalcResponse {
  vehicleSnapshot: VehicleSnapshot;
  ownerSnapshot: OwnerSnapshot;
  enrichmentLogs: EnrichmentLog[];
  providerCalls: EnrichmentLog[];
  providerQuotes: ProviderQuote[];
}

const buildCalculatorUrl = (path: string) =>
  `${CALCULATOR_BASE_URL}${path}`.trim();

export const normalizeCalcPayload = (
  form: CalcRequest
): NormalizedCalcRequest => ({
  ...form,
  experience: form.experience === "" ? 0 : Number(form.experience),
  maxWeightF1: form.maxWeightF1 === "" ? 0 : Number(form.maxWeightF1),
});

const post = async <TResponse, TPayload>(
  jwt: string,
  url: string,
  payload: TPayload
): Promise<TResponse> => {
  const body = JSON.stringify(payload);
  /**
   * This makes the exact sent payload visible in the console.
   * In Network tab, check the POST request -> Payload tab.
   */
  console.log("[CalculatorService] POST", url);
  console.log("[CalculatorService] Payload", JSON.parse(body));

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");

    throw new Error(
      `HTTP ${response.status}: ${response.statusText}${
        errorText ? ` - ${errorText}` : ""
      }`
    );
  }

  return response.json() as Promise<TResponse>;
};

const CalculatorService = {
  calculateAggregatedPremium: async (
    jwt: string,
    form: CalcRequest
  ): Promise<AggregatorResponse> => {
    const payload = normalizeCalcPayload(form);

    return post<AggregatorResponse, NormalizedCalcRequest>(
      jwt,
      buildCalculatorUrl("/aggregator/aggregated-premium"),
      payload
    );
  },
};

export default CalculatorService;
